import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import {
  useFilters, useModals, useSetState, useShowModal,
} from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ActivityModel, OrderItemTaskModel } from '@models/types'

import CreateOrEditOrderItemTaskModal from '@modals/CreateOrEditOrderItemTaskModal'
import ManageActivityModal from '@modals/ManageActivityModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditOrderItemTaskModal: false,
  showManageActivityModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: string, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditOrderItemTaskModal: {
      closeModal: () => setState({ showCreateOrEditOrderItemTaskModal: false }),
      completeOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateOrderItemTask: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditOrderItemTaskModal,
    showManageActivityModal,
  } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditOrderItemTaskModal: (payload: { teamMember: Partial<OrderItemTaskModel> }) => {
        setModalData('CreateOrEditOrderItemTaskModal', payload)
        setState({ showCreateOrEditOrderItemTaskModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
    },
    defaultFilters,
  }), [topModalKey])

  useShowModal({
    modalKey: 'CreateOrEditOrderItemTaskModal',
    options: {
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        {/*
         // @ts-expect-error TS2741 */}
        <CreateOrEditOrderItemTaskModal
          callbacks={callbacks('CreateOrEditOrderItemTaskModal', setState)}
          showModal={showCreateOrEditOrderItemTaskModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
